import { useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { employees as seedEmployees, onboardingChecklist } from "../lib/mockData";
import { Mail, FileCheck2, IdCard, Laptop, Briefcase, UserCog, Wallet, GraduationCap, Users2 } from "lucide-react";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";

const DemoHeader = () => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo 6</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">HR & Employee Onboarding</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">
      A digital onboarding checklist for every new hire, plus a company-wide HR view — attendance, leave, training and recruitment.
    </p>
  </div>
);

const KPI = ({ icon: Icon, label, value, hint }) => (
  <Card className="glass"><CardContent className="p-5">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4"/></div>
    </div>
    <div className="mt-2 font-display text-2xl font-bold">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </CardContent></Card>
);

const OnboardingView = () => {
  const [tasks, setTasks] = useState(onboardingChecklist);
  const toggle = (id) => setTasks((T) => T.map((t) => t.id === id ? { ...t, done: !t.done } : t));
  const doneCount = tasks.filter((t) => t.done).length;
  const pct = Math.round((doneCount / tasks.length) * 100);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="glass lg:col-span-1"><CardContent className="p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary">New hire</div>
          <div className="font-display text-lg font-semibold">Yaw Boadu · Support Agent</div>
          <div className="text-xs text-muted-foreground">Start date: 10 Feb 2026 · Manager: Kwame Nkrumah</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Onboarding progress</div>
          <div className="flex items-center gap-3">
            <Progress value={pct} className="flex-1"/><span className="text-sm font-bold">{pct}%</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><Mail className="w-4 h-4 text-accent"/> Welcome email</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><FileCheck2 className="w-4 h-4 text-accent"/> Contract</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><IdCard className="w-4 h-4 text-accent"/> Ghana Card</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><Laptop className="w-4 h-4 text-accent"/> Laptop</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><Briefcase className="w-4 h-4 text-accent"/> Department</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><UserCog className="w-4 h-4 text-accent"/> Manager</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><Wallet className="w-4 h-4 text-accent"/> Payroll</div>
          <div className="flex items-center gap-2 rounded-lg bg-muted/40 p-3"><GraduationCap className="w-4 h-4 text-accent"/> Training</div>
        </div>
      </CardContent></Card>

      <Card className="glass lg:col-span-2"><CardContent className="p-6">
        <div className="font-display text-lg font-semibold mb-4">Onboarding checklist</div>
        <div className="space-y-2">
          {tasks.map((t) => (
            <label key={t.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 hover:bg-muted/40 transition-colors" data-testid={`task-${t.id}`}>
              <Checkbox checked={t.done} onCheckedChange={() => toggle(t.id)}/>
              <span className={`text-sm ${t.done ? "line-through text-muted-foreground" : ""}`}>{t.label}</span>
              {t.done && <Badge className="ml-auto bg-accent text-accent-foreground">Done</Badge>}
            </label>
          ))}
        </div>
      </CardContent></Card>
    </div>
  );
};

const HRDashboard = () => {
  const active = seedEmployees.filter((e) => e.status === "Active").length;
  const onboarding = seedEmployees.filter((e) => e.status === "Onboarding").length;
  const onLeave = seedEmployees.filter((e) => e.status === "Leave").length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <KPI icon={Users2} label="Employees" value={seedEmployees.length} hint={`${active} active`}/>
        <KPI icon={Briefcase} label="Onboarding" value={onboarding} hint="Complete their setup"/>
        <KPI icon={UserCog} label="On leave" value={onLeave} hint="Approved"/>
        <KPI icon={GraduationCap} label="Training complete" value="78%" hint="Company-wide"/>
      </div>

      <Card className="glass"><CardContent className="p-6">
        <div className="font-display text-lg font-semibold mb-3">Staff directory</div>
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-12 text-xs uppercase tracking-widest bg-muted/60 px-4 py-2">
            <div className="col-span-4">Name</div>
            <div className="col-span-3">Department</div>
            <div className="col-span-2">Manager</div>
            <div className="col-span-1 text-right">Leave</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {seedEmployees.map((e) => (
            <div key={e.id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-t border-border">
              <div className="col-span-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-bold">
                  {e.name.split(" ").map((n) => n[0]).join("").slice(0,2)}
                </div>
                <div>
                  <div className="font-medium">{e.name}</div>
                  <div className="text-xs text-muted-foreground">{e.role}</div>
                </div>
              </div>
              <div className="col-span-3">{e.dept}</div>
              <div className="col-span-2 text-muted-foreground">{e.manager}</div>
              <div className="col-span-1 text-right">{e.leave}</div>
              <div className="col-span-2 text-right">
                <Badge className={e.status === "Active" ? "bg-accent text-accent-foreground"
                  : e.status === "Onboarding" ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground"}>{e.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass"><CardContent className="p-6">
          <div className="font-display text-lg font-semibold mb-3">Leave requests</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between p-3 rounded-lg bg-muted/40"><span>Abena Owusu · 3 days annual leave</span><Badge>Approved</Badge></div>
            <div className="flex justify-between p-3 rounded-lg bg-muted/40"><span>Kofi Adjei · 1 day sick leave</span><Badge>Approved</Badge></div>
            <div className="flex justify-between p-3 rounded-lg bg-muted/40"><span>Michael Tetteh · 5 days casual</span><Badge variant="secondary">Pending</Badge></div>
          </div>
        </CardContent></Card>
        <Card className="glass"><CardContent className="p-6">
          <div className="font-display text-lg font-semibold mb-3">Recruitment</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between p-3 rounded-lg bg-muted/40"><span>Senior Developer · Accra</span><Badge>4 candidates</Badge></div>
            <div className="flex justify-between p-3 rounded-lg bg-muted/40"><span>Marketing Intern · Kumasi</span><Badge>11 candidates</Badge></div>
            <div className="flex justify-between p-3 rounded-lg bg-muted/40"><span>Support Agent · Tamale</span><Badge>2 candidates</Badge></div>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default function Demo6HR() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader/>
      <Tabs defaultValue="onboarding" className="w-full">
        <TabsList data-testid="hr-tabs">
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">New hire onboarding</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-hr-dashboard">HR dashboard</TabsTrigger>
        </TabsList>
        <TabsContent value="onboarding"><OnboardingView/></TabsContent>
        <TabsContent value="dashboard"><HRDashboard/></TabsContent>
      </Tabs>

      <BeforeAfter demoId={6}/>
      <ConsultationCTA demoLabel="HR & Onboarding"/>
    </div>
  );
}
